import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { connectMessaging, onMessagingEvent } from '@/lib/messagingSocket';
import { Button } from '@/components/ui/button';
import UserAvatar from '@/components/UserAvatar';
import { Send, MessageSquare, ExternalLink, AtSign } from 'lucide-react';

// Object-attached chat (dashboards/reports/insights) — replaces Discussions.
// Supports @mentions: type "@" and a name to pick a teammate; the picked users
// are sent as mention ids so they get notified.
export default function ObjectChat({ objectType, objectId, label }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const me = user?.user_id;
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [roster, setRoster] = useState([]);
  const [mentioned, setMentioned] = useState([]); // [{user_id, name}]
  const [mentionQuery, setMentionQuery] = useState(null); // active @token or null
  const [mentionIndex, setMentionIndex] = useState(0);
  const scrollRef = useRef(null);
  const convIdRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    connectMessaging();
    api.getMessagingMentionableUsers().then(r => { if (mounted) setRoster(r?.users || []); }).catch(() => {});
    (async () => {
      const c = await api.getObjectConversation(objectType, objectId, label);
      if (!mounted) return;
      setConv(c); convIdRef.current = c.conversation_id;
      const r = await api.getMessages(c.conversation_id);
      setMessages(r.messages || []);
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    })();
    const off = onMessagingEvent((evt) => {
      if (evt.type === 'message' && evt.conversation_id === convIdRef.current) {
        setMessages(m => [...m, evt.message]);
        setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
      } else if (evt.type === 'reaction') {
        setMessages(m => m.map(x => x.message_id === evt.message_id ? { ...x, reactions: evt.reactions } : x));
      }
    });
    return () => { mounted = false; off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectType, objectId]);

  const nameOf = (u) => u.name || u.email || 'user';

  // Auto-grow the composer as the user types (single line → multi-line), capped
  // so a very long message scrolls instead of pushing the thread off-screen.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  const filteredRoster = mentionQuery === null ? [] : roster.filter(u => {
    const q = mentionQuery.toLowerCase();
    return !q || nameOf(u).toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  }).slice(0, 6);

  const onTextChange = (val) => {
    setText(val);
    // Detect an active "@token" ending at the caret (simple: last @ run).
    const m = /(?:^|\s)@([\w.\-]*)$/.exec(val);
    if (m) { setMentionQuery(m[1]); setMentionIndex(0); }
    else setMentionQuery(null);
  };

  const pickMention = (u) => {
    // Replace the trailing "@token" with "@Name ".
    const display = nameOf(u);
    const next = text.replace(/(^|\s)@([\w.\-]*)$/, (_, pre) => `${pre}@${display} `);
    setText(next);
    setMentioned(prev => prev.some(x => x.user_id === u.user_id) ? prev : [...prev, { user_id: u.user_id, name: display }]);
    setMentionQuery(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const send = async () => {
    const t = text.trim(); if (!t || !conv) return;
    // Only send mentions whose "@Name" survived in the final text.
    const ids = mentioned.filter(mm => t.includes(`@${mm.name}`)).map(mm => mm.user_id);
    setText(''); setMentioned([]); setMentionQuery(null);
    await api.sendMessage(conv.conversation_id, { text: t, mentions: ids });
  };

  const onKeyDown = (e) => {
    if (mentionQuery !== null && filteredRoster.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => (i + 1) % filteredRoster.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => (i - 1 + filteredRoster.length) % filteredRoster.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(filteredRoster[mentionIndex]); return; }
      if (e.key === 'Escape') { setMentionQuery(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="object-chat">
      <div className="flex items-center gap-2 px-1 pb-2 text-sm font-heading font-semibold"><MessageSquare className="w-4 h-4 text-cyan-400" /> {label || 'Discussion'}</div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[160px]">
        {messages.map(m => (
          <div key={m.message_id} className={`flex ${m.sender_id === me ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[80%]">
              {m.sender_id !== me && <span className="text-[10px] text-muted-foreground">{m.sender_name || m.sender?.name}</span>}
              <div className={`rounded-2xl px-3 py-1.5 text-sm ${m.sender_id === me ? 'bg-indigo-500/25' : 'bg-muted/50'}`}>{m.text}</div>
              {m.attachment?.type === 'shared_view' && (
                <Button size="sm" variant="outline" className="mt-1 h-7 text-[11px] gap-1 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
                  onClick={() => navigate(m.attachment.route)} data-testid={`open-shared-view-${m.message_id}`}>
                  <ExternalLink className="w-3 h-3" /> Open this view{m.attachment.label ? `: ${m.attachment.label}` : ''}
                </Button>
              )}
            </div>
          </div>
        ))}
        {messages.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Start the discussion on this {objectType}. Type @ to mention a teammate.</p>}
      </div>
      <div className="relative flex items-end gap-2 pt-2">
        {mentionQuery !== null && filteredRoster.length > 0 && (
          <div className="absolute bottom-12 left-0 z-50 w-64 max-h-56 overflow-y-auto rounded-xl border border-border/50 bg-popover shadow-xl p-1" data-testid="mention-list">
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><AtSign className="w-3 h-3" /> Mention</div>
            {filteredRoster.map((u, i) => (
              <button key={u.user_id} onMouseDown={(e) => { e.preventDefault(); pickMention(u); }}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left ${i === mentionIndex ? 'bg-primary/15' : 'hover:bg-muted/40'}`}
                data-testid={`mention-option-${u.user_id}`}>
                <UserAvatar name={nameOf(u)} src={u.avatarUrl} size={22} />
                <div className="min-w-0">
                  <div className="text-xs text-foreground truncate">{nameOf(u)}</div>
                  {u.email && <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>}
                </div>
              </button>
            ))}
          </div>
        )}
        <textarea ref={inputRef} value={text} onChange={e => onTextChange(e.target.value)} onKeyDown={onKeyDown}
          rows={1}
          placeholder={`Message about this ${objectType}… (type @ to mention, Shift+Enter for a new line)`}
          className="flex-1 resize-none max-h-40 overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          data-testid="object-chat-input" />
        <Button onClick={send} disabled={!text.trim()} data-testid="object-chat-send"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
